import { useAIChatbotAccess } from "@faire/help-center-self-serve/hooks/useAIChatbotAccess";
import { useBrandMilestone } from "@faire/help-center-utils/hooks/useBrandMilestones";
import { useRetailerMilestone } from "@faire/help-center-utils/hooks/useRetailerMilestones";
import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { getIsUserImpersonating } from "@faire/help-center-utils/serialized-data/getIsUserImpersonating";
import {
  getRetailerToken,
  isRetailer,
} from "@faire/help-center-utils/serialized-data/getRetailer";
import { logError } from "@faire/web--source/common/logging";
import { isProduction } from "@faire/web--source/common/user-agent/isProduction";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { useQuery as useArticleLocationTagsQuery } from "@faire/web-api--source/api/help-center/chat-bot/article-location-tags/get-hooks";
import { useQuery as useChatbotAuthQuery } from "@faire/web-api--source/api/help-center/chat-bot/auth/get-hooks";
import { trackHelpCenterAiChatbotWidgetOpenView } from "@faire/web-api--source/events/helpCenter/view/aiChatbotWidgetOpen";
import { QueryParameters as SupportQueryParameters } from "@faire/web-api--source/routes/support";
import { route as supportSelfServeChatCsat } from "@faire/web-api--source/routes/support/self-serve/chat-csat";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useChatbotStaticMetadata } from "../useChatbotStaticMetadata";

import { DECAGON_SCRIPT_URL, DecagonHandoffTags } from "./consts";
import { useEnvironment } from "./useEnvironment";

const TEN_MINUTES_IN_MS = 10 * 60 * 1000; // 10 minutes

export const useDecagon = () => {
  const { faire_direct_widget_chat } = useQueryParams(
    SupportQueryParameters.parse
  );
  const retailerToken = getRetailerToken();
  const brandToken = getBrandToken();
  const isUserImpersonating = getIsUserImpersonating();
  const { pathname } = useLocation();
  const { hasAIChatbotAccess, isAccessControlLoading } = useAIChatbotAccess();
  const {
    data: chatbotAuthResponse,
    refetch: refetchChatbotAuth,
    isLoading: isChatbotAuthLoading,
  } = useChatbotAuthQuery({
    enabled: hasAIChatbotAccess,
  });
  const {
    data: articleLocationResponse,
    isLoading: isArticleLocationTagsLoading,
  } = useArticleLocationTagsQuery({
    enabled: hasAIChatbotAccess,
  });

  const metadata = useChatbotStaticMetadata();
  const { tags } = articleLocationResponse ?? {};
  const { signature, expires_at, user_token, jwt } = chatbotAuthResponse ?? {};

  const { setHasOccurred: setRetailerChatbotTooltipOccurred } =
    useRetailerMilestone("RETAILER_DECAGON_AI_CHATBOT_TOOLTIP_SEEN");
  const { setHasOccurred: setBrandChatbotTooltipOccurred } = useBrandMilestone(
    "BRAND_DECAGON_AI_CHATBOT_TOOLTIP_SEEN"
  );
  const env = useEnvironment();

  const setRefreshChatbotAuth = (expires_at: number | undefined) => {
    if (!expires_at) {
      return;
    }
    // check if current time is 10 minutes before expires_at
    const currentTime = new Date().getTime();
    const timeDifference = expires_at * 1000 - currentTime;
    if (timeDifference <= TEN_MINUTES_IN_MS) {
      refetchChatbotAuth().then(({ data }) => {
        const { signature, expires_at, user_token, jwt } = data ?? {};
        window.duet.setUserIdAuth(signature, expires_at);
        window.duet.setUserId(user_token);
        window.duet.setMetadata({
          ...metadata,
          token: jwt,
          env,
        });
      });
    }
  };

  useEffect(() => {
    if (pathname === supportSelfServeChatCsat) {
      return;
    }

    if (
      isAccessControlLoading ||
      isArticleLocationTagsLoading ||
      isChatbotAuthLoading
    ) {
      return;
    }

    // If the user is not impersonating and we don't have a jwt, we don't want to load the chatbot
    if (!isUserImpersonating && !jwt) {
      return;
    }

    try {
      if (hasAIChatbotAccess) {
        const script = document.createElement("script");
        script.src = DECAGON_SCRIPT_URL;
        script.async = true;
        document.body.appendChild(script);
        script.addEventListener("load", () => {
          const decagonMetadata = {
            ...metadata,
            user_article_location_tags: tags,
            token: jwt,
            env,
          };
          if (faire_direct_widget_chat === "1") {
            decagonMetadata.handoff_tags =
              DecagonHandoffTags.BrandFaireDirectWidgetChat;
          }
          window.duet.setMetadata(decagonMetadata);
          window.duet.setUserId(user_token);
          window.duet.setUserIdAuth(signature, expires_at);
          window.duet.setChatSessionDuration(1); // set the chat session duration to 1 hour
          window.duet.setEventListener((event) => {
            if (event.eventName === "DUET_INITIALIZE") {
              if (isRetailer()) {
                setRetailerChatbotTooltipOccurred();
              } else {
                setBrandChatbotTooltipOccurred();
              }
              trackHelpCenterAiChatbotWidgetOpenView(
                retailerToken ?? "",
                brandToken ?? "",
                pathname,
                ""
              );
              setRefreshChatbotAuth(expires_at);
            }
            if (event.eventName === "DUET_USER_INPUT") {
              setRefreshChatbotAuth(expires_at);
            }
          });
          if (!isProduction()) {
            window.duet.setIsConversationUrlVisible(true);
          }
          if (faire_direct_widget_chat === "1") {
            window.duet.open();
          }
        });
        return () => {
          document.body.removeChild(script);
        };
      }
    } catch (error) {
      logError(`Error loading Decagon script, ${error}`, {
        data: {
          pathname,
          retailerToken,
          brandToken,
          metadata,
        },
        autoFingerprint: true,
      });
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAIChatbotAccess, isArticleLocationTagsLoading, isChatbotAuthLoading]);
};
